import dedent from 'ts-dedent'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  Language,
  Provider,
  QueryAndOutputs,
  TableSchema,
  WorkflowComponentCheckInputValuesParams,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType,
  WorkflowParameterValueErrorType
} from '../types'
import { buildJoinColumnList, buildJoinOutputSchema, validateAtLeastOneColumnSelected } from '../utils'
import { DBricksConnection } from '../workflow/connections/databricks'
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent'

const predicates = new Map([
  ['intersects', 'ST_INTERSECTS'],
  ['contains', 'ST_CONTAINS'],
  ['touches', 'ST_TOUCHES'],
  ['covers', 'ST_COVERS']
])

const predicatesDatabricks = new Map([
  ['intersects', 'ST_INTERSECTS'],
  ['contains', 'ST_CONTAINS'],
  ['touches', 'SEDONA_ST_TOUCHES'],
  ['covers', 'SEDONA_ST_COVERS']
])

class SpatialJoin extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.spatialjoin',
        group: 'Joins',
        title: 'Spatial Join',
        description: `
    This component performs a spatial join between two tables, based on a spatial predicate.
    `,
        starred: true
      },
      [
        {
          name: 'maintable',
          type: WorkflowComponentParamType.InputTable,
          title: 'Left table'
        },
        {
          name: 'secondarytable',
          type: WorkflowComponentParamType.InputTable,
          title: 'Right table'
        },
        {
          name: 'maintablejoincolumn',
          type: WorkflowComponentParamType.Column,
          parent: 'maintable',
          title: 'Key geo column in left table',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'maintablecolumns',
          type: WorkflowComponentParamType.ColumnsForJoin,
          title: 'Left table columns',
          parent: 'maintable',
          optional: true
        },
        {
          name: 'secondarytablejoincolumn',
          type: WorkflowComponentParamType.Column,
          parent: 'secondarytable',
          title: 'Key geo column in right table',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'secondarytablecolumns',
          type: WorkflowComponentParamType.ColumnsForJoin,
          title: 'Right table columns',
          parent: 'secondarytable',
          optional: true
        },
        {
          name: 'jointype',
          type: WorkflowComponentParamType.Selection,
          title: 'Join type',
          default: 'inner',
          options: ['inner', 'left', 'right', 'full']
        },
        {
          name: 'predicate',
          type: WorkflowComponentParamType.Selection,
          title: 'Spatial predicate',
          default: predicates.keys().next().value,
          optional: true,
          options: Array.from(predicates.keys())
        }
      ]
    )
  }

  async getCode({
    workflow,
    params,
    connection,
    componentName,
    codeType,
    schemas
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs | null> {
    const outputTablename = connection.getOutputTableName(workflow.id, componentName, params, 'result', codeType)
    const preparedParams = this._prepareParameters(params)

    const mainTable = preparedParams.maintable as string
    const secondaryTable = preparedParams.secondarytable as string
    const mainTableColumns = preparedParams.maintablecolumns as string
    const secondaryTableColumns = preparedParams.secondarytablecolumns as string
    const mainTableJoinColumn = preparedParams.maintablejoincolumn as string
    const secondaryTableJoinColumn = preparedParams.secondarytablejoincolumn as string
    const joinType = preparedParams.jointype as string
    const predicate = preparedParams.predicate as string

    const cols = buildJoinColumnList(mainTableColumns, secondaryTableColumns, '_main', '_joined')

    let select = ''
    if (connection.type === Provider.DatabricksRest) {
      let geomFromWKBFunct = 'ST_GEOMFROMWKB'
      if (predicate === 'touches' || predicate === 'covers') {
        geomFromWKBFunct = 'SEDONA_ST_GEOMFROMWKB'
      }
      const leftOutputTableName = connection.quoteName(outputTablename + '_left')
      const rightOutputTableName = connection.quoteName(outputTablename + '_right')

      select = `WITH _main AS (
        SELECT /*+ REPARTITION(40, ${mainTableJoinColumn}) */
        *, ${geomFromWKBFunct}(${mainTableJoinColumn}) AS ${mainTableJoinColumn}_processed FROM ${leftOutputTableName}
      ),
      _joined AS (
        SELECT /*+ REPARTITION(40, ${secondaryTableJoinColumn}) */
        *, ${geomFromWKBFunct}(${secondaryTableJoinColumn}) AS ${secondaryTableJoinColumn}_processed FROM ${rightOutputTableName}
      )
      SELECT /*+ REPARTITION(40) */
        ${cols.join(', ')}
      FROM
        _main
      ${joinType.toUpperCase()} JOIN _joined ON
          (_main.__carto_bounds_xmax >= _joined.__carto_bounds_xmin
          AND _main.__carto_bounds_xmin <= _joined.__carto_bounds_xmax
          AND _main.__carto_bounds_ymax >= _joined.__carto_bounds_ymin
          AND _main.__carto_bounds_ymin <= _joined.__carto_bounds_ymax)
        AND
          ${predicatesDatabricks.get(predicate)}(
            _main.${mainTableJoinColumn}_processed,
            _joined.${secondaryTableJoinColumn}_processed
          )`
    } else {
      select = `
        SELECT
          ${cols.join(', ')}
        FROM
          ${connection.quoteName(mainTable)} AS _main
        ${joinType.toUpperCase()} JOIN
          ${connection.quoteName(secondaryTable)} AS _joined
        ON
          ${predicates.get(predicate)}(
            _main.${mainTableJoinColumn},
            _joined.${secondaryTableJoinColumn}
          )`
    }
    let createTable = connection.getCreateTempTableQuery(outputTablename, select, codeType, null)

    let query = ''
    if (connection.type === Provider.DatabricksRest) {
      const leftOutputTableName = connection.quoteName(outputTablename + '_left')
      const rightOutputTableName = connection.quoteName(outputTablename + '_right')
      const generateSpatialFilterTablesQuery = (connection as DBricksConnection).generateSpatialFilterTables(
        connection.quoteName(mainTable),
        connection.quoteName(secondaryTable),
        mainTableJoinColumn,
        secondaryTableJoinColumn,
        leftOutputTableName,
        rightOutputTableName
      )
      createTable = (connection as DBricksConnection).wrapSQLCodeAsPythonCall(createTable)
      query = dedent`
        ${generateSpatialFilterTablesQuery}
        ${createTable}
      `
    } else {
      query = createTable
    }

    return {
      query,
      outputs: { result: outputTablename }
    }
  }

  checkInputValues({
    params,
    resolvedParamValues,
    connection,
    dryRun,
    schemas,
    variables
  }: WorkflowComponentCheckInputValuesParams) {
    const problems = super.checkInputValues({
      params,
      resolvedParamValues,
      connection,
      dryRun,
      schemas,
      variables
    })

    // Check if at least one column is selected from either table
    const columnSelectionError = validateAtLeastOneColumnSelected(
      resolvedParamValues.maintablecolumns as string | null,
      resolvedParamValues.secondarytablecolumns as string | null,
      'maintablecolumns'
    )
    if (columnSelectionError) {
      problems.push(columnSelectionError)
    }

    return problems
  }

  language(provider: Provider): Language {
    if (provider === Provider.DatabricksRest) {
      return Language.Python
    }
    return Language.SQL
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getVersion(provider: Provider): string {
    return '2'
  }

  getOutputSchema({ columns, params, connection }: GetOutputSchemaParams): TableSchema {
    return buildJoinOutputSchema(
      columns.maintable,
      columns.secondarytable,
      params.maintablecolumns as string,
      params.secondarytablecolumns as string
    )
  }
  supportedProviders(): Provider[] {
    return [Provider.Redshift, Provider.Postgres, Provider.BigQuery, Provider.DatabricksRest]
  }
}

export { SpatialJoin }
